import {
  useState, useMemo, useRef 
} from 'react';
import {
  X,
  Plus,
  Trash2,
  Search,
  GripVertical,
  UtensilsCrossed,
  MapPin,
  Calendar,
  Users,
  AlertTriangle,
  Leaf,
  DollarSign,
  Save,
  Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/classnames';
import type {
  Menu, MenuItem 
} from '../../types';
import {
  MENU_TYPE_LABELS, MENU_STATUS_LABELS, MENU_STATUS_COLORS 
} from '../../types';
import type { Recipe } from '@/features/recipes';

interface MenuDetailModalProps {
  readonly menu: Menu | null;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSave?: (menuId: string, items: MenuItem[]) => void;
  readonly availableRecipes?: Recipe[];
  readonly onViewRecipe?: (recipe: Recipe) => void;
}

const ALLERGEN_LABELS: Record<string, string> = {
  milk: 'Milk',
  eggs: 'Eggs',
  fish: 'Fish',
  shellfish: 'Shellfish',
  'tree-nuts': 'Tree Nuts',
  peanuts: 'Peanuts',
  wheat: 'Wheat',
  soybeans: 'Soybeans',
  sesame: 'Sesame',
};

type TabId = 'items' | 'add-recipes';

export function MenuDetailModal({
  menu,
  isOpen,
  onClose,
  onSave,
  availableRecipes = [],
  onViewRecipe,
}: MenuDetailModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('items');
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [recipeSearch, setRecipeSearch] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const itemIdCounter = useRef(0);

  // Initialize menu items when menu changes
  useMemo(() => {
    if (menu) {
      setMenuItems([...menu.items]);
      setHasChanges(false);
    }
  }, [menu]);

  if (!isOpen || !menu) return null;

  // Filter recipes that aren't already in the menu
  const existingRecipeIds = new Set(menuItems.map((item) => item.recipeId));
  const filteredRecipes = availableRecipes.filter(
    (recipe) =>
      recipe.type === 'finished' &&
      !existingRecipeIds.has(recipe.recipeId) &&
      recipe.name.toLowerCase().includes(recipeSearch.toLowerCase())
  );

  const handleAddRecipe = (recipe: Recipe) => {
    itemIdCounter.current += 1;
    const newItem: MenuItem = {
      menuItemId: `mi-${Date.now()}-${itemIdCounter.current}`,
      recipeId: recipe.recipeId,
      recipeName: recipe.name,
      category: recipe.category,
      price: recipe.costPerServing ? Math.round(recipe.costPerServing * 3.5 * 100) / 100 : undefined,
      isAvailable: true,
      allergens: recipe.allergens,
      dietaryFlags: recipe.dietaryFlags,
      sortOrder: menuItems.length,
    };
    setMenuItems([...menuItems, newItem]);
    setHasChanges(true);
    setRecipeSearch('');
  };

  const handleRemoveItem = (menuItemId: string) => {
    setMenuItems(menuItems.filter((item) => item.menuItemId !== menuItemId));
    setHasChanges(true);
  };

  const handleToggleAvailability = (menuItemId: string) => {
    setMenuItems(
      menuItems.map((item) =>
        item.menuItemId === menuItemId ? {
          ...item,
          isAvailable: !item.isAvailable 
        } : item
      )
    );
    setHasChanges(true);
  };

  const handleUpdatePrice = (menuItemId: string, price: number) => {
    setMenuItems(
      menuItems.map((item) =>
        item.menuItemId === menuItemId ? {
          ...item,
          price 
        } : item
      )
    );
    setHasChanges(true);
  };

  const handleSave = () => {
    if (onSave) {
      onSave(menu.menuId, menuItems);
    }
    setHasChanges(false);
  };

  const handleViewRecipe = (recipeId: string) => {
    const recipe = availableRecipes.find((r) => r.recipeId === recipeId);
    if (recipe && onViewRecipe) {
      onViewRecipe(recipe);
    }
  };

  const tabs: {
    id: TabId;
    label: string 
  }[] = [
    {
      id: 'items',
      label: `Menu Items (${menuItems.length})` 
    },
    {
      id: 'add-recipes',
      label: 'Add Recipes' 
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-4xl max-h-[90vh] flex flex-col rounded-lg bg-background shadow-xl">
        {/* Header */}
        <div className="flex-shrink-0 border-b px-6 py-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold">{menu.name}</h2>
                <span className={cn('rounded-full px-2 py-1 text-xs font-medium', MENU_STATUS_COLORS[menu.status])}>
                  {MENU_STATUS_LABELS[menu.status]}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {menu.venueName}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {MENU_TYPE_LABELS[menu.type]}
                </span>
                {menu.defaultPax && (
                  <span className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {menu.defaultPax} pax
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-2 hover:bg-muted transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex-shrink-0 border-b px-6">
          <nav className="flex gap-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'border-b-2 px-1 py-3 text-sm font-medium transition-colors',
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'items' && (
            <div className="space-y-4">
              {menuItems.length === 0 ? (
                <div className="text-center py-12">
                  <UtensilsCrossed className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">No items in this menu</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Add recipes from your library to build this menu
                  </p>
                  <Button onClick={() => setActiveTab('add-recipes')}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Recipes
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {menuItems.map((item) => (
                    <div
                      key={item.menuItemId}
                      className={cn(
                        'flex items-center gap-3 rounded-lg border p-3 transition-colors',
                        !item.isAvailable && 'opacity-60 bg-muted/30'
                      )}
                    >
                      <div className="flex-shrink-0 cursor-grab text-muted-foreground">
                        <GripVertical className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleViewRecipe(item.recipeId)}
                            className="font-medium hover:text-primary hover:underline transition-colors"
                          >
                            {item.recipeName}
                          </button>
                          {!item.isAvailable && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                              Unavailable
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                          <span>{item.category}</span>
                          {item.allergens.length > 0 && (
                            <span className="flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3 text-amber-500" />
                              {item.allergens.slice(0, 2).map((a) => ALLERGEN_LABELS[a] ?? a).join(', ')}
                              {item.allergens.length > 2 && ` +${item.allergens.length - 2}`}
                            </span>
                          )}
                          {item.dietaryFlags.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Leaf className="h-3 w-3 text-green-500" />
                              {item.dietaryFlags.slice(0, 2).join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-4 w-4 text-muted-foreground" />
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.price ?? ''}
                            onChange={(e) => handleUpdatePrice(item.menuItemId, parseFloat(e.target.value) || 0)}
                            placeholder="0.00"
                            className="w-20 rounded border bg-background px-2 py-1 text-sm text-right"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleAvailability(item.menuItemId)}
                          className={item.isAvailable ? 'text-green-600' : 'text-red-600'}
                        >
                          {item.isAvailable ? 'Available' : 'Unavailable'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewRecipe(item.recipeId)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveItem(item.menuItemId)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'add-recipes' && (
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={recipeSearch}
                  onChange={(e) => setRecipeSearch(e.target.value)}
                  placeholder="Search recipes to add..."
                  className="w-full rounded-md border bg-background py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <p className="text-sm text-muted-foreground">
                Showing {filteredRecipes.length} available menu items (finished recipes)
              </p>

              {filteredRecipes.length === 0 ? (
                <div className="text-center py-8">
                  <UtensilsCrossed className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">
                    {recipeSearch
                      ? 'No matching recipes found'
                      : 'All available recipes are already in this menu'}
                  </p>
                </div>
              ) : (
                <div className="grid gap-2 max-h-96 overflow-y-auto">
                  {filteredRecipes.map((recipe) => (
                    <div
                      key={recipe.recipeId}
                      className="flex items-center justify-between rounded-lg border p-3 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{recipe.name}</p>
                        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                          <span>{recipe.category}</span>
                          {recipe.cuisine && <span>· {recipe.cuisine}</span>}
                          {recipe.costPerServing && (
                            <span className="text-green-600">
                              ${recipe.costPerServing.toFixed(2)} cost
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {recipe.allergens.slice(0, 3).map((allergen) => (
                            <span
                              key={allergen}
                              className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded"
                            >
                              {ALLERGEN_LABELS[allergen] ?? allergen}
                            </span>
                          ))}
                          {recipe.dietaryFlags.slice(0, 2).map((flag) => (
                            <span
                              key={flag}
                              className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded"
                            >
                              {flag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <Button onClick={() => handleAddRecipe(recipe)} size="sm">
                        <Plus className="mr-1 h-4 w-4" />
                        Add
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between border-t px-6 py-4">
          <div className="text-sm text-muted-foreground">
            {menuItems.length} items · {menuItems.filter((i) => i.isAvailable).length} available
            {hasChanges && <span className="ml-2 text-amber-600">· Unsaved changes</span>}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              {hasChanges ? 'Discard' : 'Close'}
            </Button>
            {hasChanges && (
              <Button onClick={handleSave}>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
